import { useQueryClient } from '@tanstack/react-query';
import { useQueryStates } from 'nuqs';
import { useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/auth-context';
import type { WishlistCreator } from '@fluenceur/types';
import { useContactSummaryQuery } from '@/hooks/queries/use-contact-summary-query';
import { contactResolveQueryKey } from '@/hooks/queries/use-resolve-contact-query';
import { useWishlistToggle } from '@/hooks/use-wishlist-toggle';
import { contactDrawerParsers } from '../contact-drawer-url-state';
import type { ContactShell } from '../contact-types';
import { platformEnumOf } from '../platform-of';
import { useResolvedContactIdentityId } from '../use-resolved-contact-identity-id';

export interface ContactWishlistStar {
  /**
   * True when this contact can be checked against / added to the wishlist — i.e.
   * a creator contact carrying a keyable source creator id. Affiliates and
   * un-keyed identities cannot be wishlisted, so the star stays inert.
   */
  canToggle: boolean;
  /** Real wishlist membership (batch-checked), not local UI state. */
  isInWishlist: boolean;
  /**
   * The full wishlist row when the contact is saved, `null` otherwise. The
   * outreach view keys the creator-outreach send on its `id`.
   */
  wishlistItem: WishlistCreator | null;
  /** Optimistically add/remove, then invalidate the wishlist + check queries. */
  toggle: () => void;
  /** True while checking membership or mutating — the star is disabled meanwhile. */
  isBusy: boolean;
  /** True while the membership check is still in flight (row unknown). */
  isChecking: boolean;
}

/**
 * Backs the identity-header favorite star with real wishlist membership.
 *
 * Reuses `useWishlistToggle` (the same hook the discovery grid star uses): it
 * batch-checks membership by the creator's source id or platform user id, and
 * add/removes via the shared wishlist actions, invalidating `['wishlist']` so
 * every star stays in sync.
 *
 * A wishlist entry is keyed by either a source creator id (present when the
 * drawer was opened from the discovery/recommendation grid) or a platform user
 * id (always available in the contact summary once the identity is resolved).
 * When the drawer is opened from the Mentions tab via `useOpenExistingCreatorSurface`
 * it carries only a username and no `contactSourceCreatorId`, so the platform user
 * id is derived from the already-cached summary instead — no extra request.
 * Other contacts (affiliates, un-keyed identities) get an inert star rather than
 * a misleading empty one.
 */
export const useContactWishlistStar = (contact: ContactShell): ContactWishlistStar => {
  const [{ contactId, contactEntry, contactSourceCreatorId, contactPlatform }] =
    useQueryStates(contactDrawerParsers);

  const identityId = useResolvedContactIdentityId();
  const { user } = useAuth();
  const { data: summary } = useContactSummaryQuery(user?.currentBrandId, identityId);
  const queryClient = useQueryClient();

  // Derive the platform user id from the already-cached contact summary when the
  // drawer was opened without a source creator id. The summary is pre-loaded by
  // GlobalContactDrawer, so no extra request is made here.
  const resolvedPlatformUserId =
    summary?.socialProfiles.find((p) => p.platform === contactPlatform)?.platformUserId ?? null;

  const creator =
    contactEntry === 'creator' && contactId && (contactSourceCreatorId || resolvedPlatformUserId)
      ? {
          username: contactId,
          sourceCreatorId: contactSourceCreatorId || undefined,
          platformUserId: resolvedPlatformUserId || undefined,
          platform: platformEnumOf(contactPlatform),
          fullName: contact.name || undefined,
          profilePictureUrl: contact.avatarUrl || undefined,
        }
      : contactEntry === 'source' && contactId
        ? {
            // A prospect the plan masks: saved by its dataset reference, with
            // no handle and no name to send.
            sourceCreatorId: contactSourceCreatorId || contactId,
            platformUserId: resolvedPlatformUserId || undefined,
            platform: platformEnumOf(contactPlatform),
            profilePictureUrl: contact.avatarUrl || undefined,
          }
        : null;

  // Adding an as-yet-unresolved creator makes the backend provision its identity
  // lazily. Capture that intent at click time so, once the add settles, we
  // re-resolve the drawer + summary and the card unlocks. Removes and
  // already-resolved contacts skip it, so the shared useWishlistToggle stays
  // untouched (this carries the identity-unlock invalidations the old save
  // prompt used to run).
  const shouldRefreshIdentityRef = useRef(false);

  const {
    isInWishlist,
    wishlistItem,
    toggle: toggleWishlist,
    isToggling,
    isLoading,
  } = useWishlistToggle(creator, {
    onSuccess: () => {
      if (shouldRefreshIdentityRef.current && contactId) {
        queryClient.invalidateQueries({
          queryKey: contactResolveQueryKey(contactEntry, contactId),
        });
        queryClient.invalidateQueries({ queryKey: ['contact-summary'] });
      }
      shouldRefreshIdentityRef.current = false;
    },
  });

  const toggle = useCallback(() => {
    shouldRefreshIdentityRef.current = !isInWishlist && !identityId;
    toggleWishlist();
  }, [isInWishlist, identityId, toggleWishlist]);

  return {
    canToggle: !!creator,
    isInWishlist,
    wishlistItem,
    toggle,
    isBusy: isToggling || isLoading,
    isChecking: isLoading,
  };
};
